<template>
  <InfuseTable
    :click-permission-id="userPermissions.GET"
    :columns="columns"
    :rows="rows"
    :loading="loading"
    :editable="editable"
    :removable="removable"
    :search="search"
    :has-more-items="hasMoreItems"
    :remove-permission-id="userPermissions.DELETE"
    :edit-permission-id="userPermissions.UPDATE_PASSWORD"
    @click-row="(row: User) => emit('click-row', row)"
    @load="emit('load')"
    @remove="(row: User) => emit('remove', row)"
    @edit="(row: User) => emit('edit', row)"
    @search="(query: string) => emit('search', query)"
  >
    <template #actions>
      <InfuseButton
        data-testid="page-create-button"
        v-permission="{ id: userPermissions.CREATE, hide: false }"
        v-if="createEnabled"
        class="w-64"
        size="medium"
        @click="emit('create')"
      >
        {{ t('e_connector.users.table.create_button') }}
      </InfuseButton>
    </template>

    <template #name="{ data }">
      <div class="min-w-[300px]">{{ data }}</div>
    </template>
  </InfuseTable>
</template>

<script lang="ts" setup>
import { InfuseButton, InfuseTable } from '@infuse/ui/components';
import { vPermission } from '@infuse/iam/permission/directives';
import type { UiTableColumn } from '@infuse/ui/domain';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { User } from '@infuse/e-connector/user/domain';
import { userPermissions } from '@infuse/api/e-connector/user';

interface Emits {
  (e: 'click-row', row: User): void;
  (e: 'create'): void;
  (e: 'load'): void;
  (e: 'remove', row: User): void;
  (e: 'edit', row: User): void;
  (e: 'search', query: string): void;
}

const emit = defineEmits<Emits>();

interface Props {
  createEnabled: boolean;
  editable: boolean;
  loading?: boolean;
  removable?: boolean;
  rows: User[];
  searchEnabled: boolean;
  searchQuery?: string;
  hasMoreItems: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  createEnabled: false,
  editable: false,
  loading: false,
  removable: false,
  searchEnabled: true,
  searchQuery: ''
});

const { t } = useI18n();

const search = computed(() =>
  !props.searchEnabled
    ? undefined
    : {
        placeholder: t('e_connector.users.table.search'),
        query: props.searchQuery
      }
);

const columns: UiTableColumn[] = [
  {
    name: 'id',
    label: () => t('e_connector.common.user.id'),
    field: 'id',
    bold: false
  },
  {
    name: 'name',
    label: () => t('e_connector.common.user.name'),
    field: 'name',
    bold: true
  }
];
</script>
